import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getDeviceSessions,
  getDeviceMealRedemptions,
  recordSessionScan,
  getDevices,
} from "../services/api";
import { useApiQuery, useApiMutation } from "../hooks/useApiQuery";
import {
  ArrowLeftIcon,
  DevicePhoneMobileIcon,
  XMarkIcon,
  CheckIcon,
  ClockIcon,
  CakeIcon,
} from "@heroicons/react/24/outline";
import { useSocket } from "../context/SocketContext";
import { useQueryClient } from "@tanstack/react-query";
import useToast from "../hooks/useToast";
const DeviceSessions = () => {
  const { deviceId } = useParams();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const socket = useSocket();

  const PAGE_SIZE = 50;

  const [page, setPage] = useState(1);
  const [customId, setCustomId] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [mealResults, setMealResults] = useState([]);

  const inputRef = useRef(null);
  const tableRef = useRef(null);

  // =====================================================
  // Devices
  // =====================================================
  const { data: allDevices = [], isLoading: devicesLoading } = useApiQuery(
    ["devices"],
    () => getDevices().then((res) => res.data),
    { staleTime: 5 * 60 * 1000 },
  );

  const device = useMemo(
    () => allDevices.find((d) => String(d._id) === String(deviceId)),
    [allDevices, deviceId],
  );

  const isMealDevice = useMemo(() => Boolean(device?.mealTypeId), [device]);

  // =====================================================
  // Records (sessions OR meal redemptions)
  // =====================================================
  const {
    data: recordsResponse,
    isLoading: recordsLoading,
    isFetching: recordsFetching,
    refetch: refetchRecords,
  } = useApiQuery(
    ["device-records", deviceId, page, isMealDevice],
    () => {
      if (isMealDevice) {
        return getDeviceMealRedemptions(deviceId, {
          page,
          limit: PAGE_SIZE,
        }).then((res) => res.data);
      }
      return getDeviceSessions(deviceId, {
        page,
        limit: PAGE_SIZE,
      }).then((res) => res.data);
    },
    {
      enabled: Boolean(deviceId) && !devicesLoading,
      staleTime: 30 * 1000,
      placeholderData: (previousData) => previousData,
    },
  );

  const records = recordsResponse?.data || [];
  const pagination = recordsResponse?.pagination || {
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  };

  // With placeholderData on, recordsLoading only true on first load
  const isInitialLoading = recordsLoading || devicesLoading;
  // =====================================================
  // Scan mutation
  // =====================================================
  const recordScanMutation = useApiMutation(
    (data) => recordSessionScan(data).then((res) => res.data),
    {
      onSuccess: (data) => {
        setCustomId("");

        // ==================================================
        // MEAL mode
        // ==================================================
        if (data?.mode === "meal") {
          const results = Array.isArray(data?.mealResults)
            ? data.mealResults
            : [];

          if (results.length === 0) {
            // Backend returned no meal results — surface its top-level message
            showError(data?.message || "No meals assigned for this attendee");
          } else {
            // Fire one toast per meal result
            results.forEach((r) => {
              if (r.allowed) {
                showSuccess(r.message || `${r.mealName || "Meal"} redeemed`);
              } else {
                showError(r.message || `${r.mealName || "Meal"} not allowed`);
              }
            });
          }

          refetchRecords();
          setTimeout(() => inputRef.current?.focus(), 50);
          return;
        }

        // ==================================================
        // ACCESS mode
        // ==================================================
      const newSession = data?.session;
if (!newSession) return;

        

        queryClient.setQueryData(
          ["device-records", deviceId, page, false],
          (oldData) => {
            if (!oldData) return oldData;
            const list = oldData.data || [];
            const exists = list.some(
              (s) => String(s._id) === String(newSession._id),
            );
            if (exists) return oldData;
            return { ...oldData, data: [...list, newSession] };
          },
        );

        setTimeout(() => inputRef.current?.focus(), 50);
      },
     onError: (error) => {
  const text =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "Scan failed";
  showError(text);
  inputRef.current?.focus();
},
      onSettled: () => setIsProcessing(false),
    },
  );

  // =====================================================
  // Focus
  // =====================================================
  // =====================================================
  // Focus — once on mount only.
  // Re-focus after scans is handled by the mutation callbacks.
  // (Do NOT depend on `records`, or every page change will
  // steal focus to the input and scroll the page to the top.)
  // =====================================================
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // =====================================================
  // Socket
  // =====================================================
  useEffect(() => {
    if (!socket || !deviceId) return;

    const handleSessionCreated = () => {
      if (!isMealDevice) refetchRecords();
    };
    const handleMealRedeemed = () => {
      if (isMealDevice) refetchRecords();
    };

    socket.on("sessionCreated", handleSessionCreated);
    socket.on("mealRedeemed", handleMealRedeemed);

    return () => {
      socket.off("sessionCreated", handleSessionCreated);
      socket.off("mealRedeemed", handleMealRedeemed);
    };
  }, [socket, deviceId, isMealDevice, refetchRecords]);

  // =====================================================
  // Handlers
  // =====================================================
  const performScan = useCallback(
    (idToScan) => {
      if (!idToScan || isProcessing) return;
      const trimmed = idToScan.trim();
     if (!trimmed) {
  showError("Please enter a User ID");
  inputRef.current?.focus();
  return;
}
     if (!device) {
  showError("Device missing");
  return;
}

      setIsProcessing(true);
      setMealResults([]);

      recordScanMutation.mutate({
        customId: trimmed,
        eventId: device.eventId?._id || device.eventId,
        deviceId: device._id,
      });
    },
    [device, isProcessing, recordScanMutation],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key !== "Enter") return;
      e.preventDefault();
      const trimmed = customId.trim();
      if (!trimmed) {
        alert("Please enter User ID");
        inputRef.current?.focus();
        return;
      }
      performScan(trimmed);
    },
    [customId, performScan],
  );

  const handleSave = useCallback(() => {
    const trimmed = customId.trim();
    if (!trimmed) {
      alert("Please enter User ID");
      inputRef.current?.focus();
      return;
    }
    performScan(trimmed);
  }, [customId, performScan]);

  const handleClear = useCallback(() => {
    setCustomId("");
    setMealResults([]);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);
  // =====================================================
  // Pagination
  // =====================================================
  // =====================================================
  // Pagination
  // =====================================================
  const goToPage = useCallback(
    (newPage) => {
      const safePage = Math.max(
        1,
        Math.min(newPage, pagination.totalPages || 1),
      );

      setPage(safePage);

      if (tableRef.current) {
        tableRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    },
    [pagination.totalPages],
  );
  // =====================================================
  // Loading
  // =====================================================
  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3" />
          <p className="text-gray-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  const isSubmitting = recordScanMutation.isPending;

  // =====================================================
  // UI
  // =====================================================
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-md text-gray-500 hover:text-[#1672b9] hover:bg-gray-50"
        >
          <ArrowLeftIcon className="w-5 h-5" />
        </button>

        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isMealDevice ? "Meal Counter" : "Sessions"} –{" "}
            {device?.name || "Device"}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {isMealDevice
              ? "Scan attendee IDs to redeem meals"
              : "Manage check-ins and check-outs for this device"}
          </p>

          {isMealDevice && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              <span className="inline-flex items-center gap-1 bg-[#1672b9]/5 text-[#1672b9] text-xs font-medium px-2.5 py-1 rounded-full">
                <CakeIcon className="w-3 h-3" />
                {device.mealTypeName}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Scan card */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        <div className="flex items-center gap-2 mb-4">
          <DevicePhoneMobileIcon className="w-5 h-5 text-[#1672b9]" />
          <h2 className="font-medium text-gray-900">
            {isMealDevice ? "Redeem a meal" : "Record a new scan"}
          </h2>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <input
              ref={inputRef}
              autoFocus
              type="text"
              value={customId}
              onChange={(e) => setCustomId(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter User ID"
              className="w-full border border-gray-200 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9]"
              disabled={isSubmitting}
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={isSubmitting}
              className="inline-flex items-center gap-1 bg-[#1672b9] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#0f5a9e] disabled:opacity-50"
            >
              <CheckIcon className="w-4 h-4" />
              {isSubmitting
                ? "Processing..."
                : isMealDevice
                  ? "Redeem"
                  : "Save"}
            </button>

            <button
              onClick={handleClear}
              className="inline-flex items-center gap-1 bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              <XMarkIcon className="w-4 h-4" />
              Clear
            </button>
          </div>
        </div>

        <p className="text-xs text-gray-400 mt-2">
          Press <kbd className="px-1 py-0.5 bg-gray-100 rounded">Enter</kbd> to
          submit manually, or just paste/scan the ID
        </p>
      </div>

      {/* Meal results (only on meal devices) */}
      {mealResults.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <CakeIcon className="w-5 h-5 text-[#1672b9]" />
            <h2 className="font-medium text-gray-900">Meal Redemptions</h2>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
              {mealResults.length} meal{mealResults.length !== 1 ? "s" : ""}
            </span>
          </div>

          <div className="space-y-2">
            {mealResults.map((result, idx) => (
              <div
                key={idx}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 ${
                  result.allowed
                    ? "border-green-200 bg-green-50"
                    : "border-amber-200 bg-amber-50"
                }`}
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    result.allowed ? "bg-green-100" : "bg-amber-100"
                  }`}
                >
                  <CheckIcon
                    className={`w-5 h-5 ${
                      result.allowed ? "text-green-600" : "text-amber-600"
                    }`}
                  />
                </div>
                <div className="flex-1">
                  <p
                    className={`text-sm font-semibold ${
                      result.allowed ? "text-green-800" : "text-amber-800"
                    }`}
                  >
                    {result.mealName || "Meal"}
                  </p>
                  <p
                    className={`text-xs ${
                      result.allowed ? "text-green-700" : "text-amber-700"
                    }`}
                  >
                    {result.message}
                  </p>
                </div>
                {result.redeemedAt && (
                  <span className="text-xs text-gray-500">
                    {new Date(result.redeemedAt).toLocaleTimeString()}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Records table */}
      <div
        ref={tableRef}
        className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-medium text-gray-900 flex items-center gap-2">
            <ClockIcon className="w-5 h-5 text-gray-400" />
            {isMealDevice ? "Meal Redemptions" : "Session History"}
            <span className="text-sm font-normal text-gray-500 ml-1">
              ({pagination.total} records)
            </span>
          </h2>
        </div>

        <div className="overflow-x-auto">
          {isMealDevice ? (
            <table className="min-w-full">
              <thead className="bg-gray-50/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    No.
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    User ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Meal
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {records.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-12 text-gray-500">
                      No meals redeemed on this device yet.
                    </td>
                  </tr>
                ) : (
                  records.map((c, idx) => (
                    <tr key={c._id} className="hover:bg-gray-50/50">
                      <td className="px-6 py-4 text-sm text-gray-500 tabular-nums">
                        {(page - 1) * PAGE_SIZE + idx + 1}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {c.customId || "—"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {c.attendeeName || "Unknown"}
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center gap-1 bg-[#1672b9]/5 text-[#1672b9] text-xs font-medium px-2.5 py-1 rounded-full">
                          <CakeIcon className="w-3 h-3" />
                          {c.mealTypeName || "—"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {c.redeemedAt
                          ? new Date(c.redeemedAt).toLocaleTimeString()
                          : "—"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {c.redeemedAt
                          ? new Date(c.redeemedAt).toLocaleDateString()
                          : "—"}
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-600">
                          USED
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          ) : (
            <table className="min-w-full">
              <thead className="bg-gray-50/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    No.
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    User ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Event
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Device
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {records.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-12 text-gray-500">
                      No sessions recorded for this device yet.
                    </td>
                  </tr>
                ) : (
                  records.map((s, idx) => (
                    <tr key={s._id} className="hover:bg-gray-50/50">
                      <td className="px-6 py-4 text-sm text-gray-500 tabular-nums">
                        {(page - 1) * PAGE_SIZE + idx + 1}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {s.customId || "—"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {s.name || "Unknown"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {s.email || "—"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {s.eventId?.name || "—"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {s.entryDevice?.name || device?.name || "—"}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            s.status === "IN"
                              ? "bg-[#1672b9]/10 text-[#1672b9]"
                              : "bg-gray-100 text-gray-500"
                          }`}
                        >
                          {s.status === "IN" ? "ENTRY" : "EXIT"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {s.entryTime
                          ? new Date(s.entryTime).toLocaleTimeString()
                          : "—"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {s.entryTime
                          ? new Date(s.entryTime).toLocaleDateString()
                          : "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
            <div className="text-sm text-gray-500">
              Page {pagination.page} of {pagination.totalPages}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => goToPage(page - 1)}
                disabled={!pagination.hasPreviousPage || recordsFetching}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40"
              >
                Previous
              </button>
              <button
                onClick={() => goToPage(page + 1)}
                disabled={!pagination.hasNextPage || recordsFetching}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeviceSessions;
